/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import {
  AfterViewInit,
  OnDestroy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';

import {
  BehaviorSubject,
  Subscription
} from 'rxjs';

import {
  WorkBlock,
  WorkType
} from '@/models/scenario/work.block';

import {Scenario}                  from '@/models/scenario/scenario';
import {Logger}                    from '@/util/logger';
import {Period}                    from '@/util/period';
import {UnDoRedo}                  from '@/util/un.do.redo';
import {DecisionPoint}             from '@/models/scenario/decision.point';
import {WorkTypeRenderer}          from '@/screens/ide/workblox/work.type.renderer';
import {WorkDecisionPointRenderer} from '@/screens/ide/workblox/work.decision.point.renderer';
import {WorkDecisionPointEditor}   from '@/screens/ide/workblox/work.decision.point.editor';
import {WorkActiveDaysRenderer}    from '@/screens/ide/workblox/work.active.days.renderer';
import {WorkActiveDaysEditor}      from '@/screens/ide/workblox/work.active.days.editor';
import {NameRenderer}              from '@/helpers/name.renderer';
import {DescriptionRenderer}       from '@/helpers/description.renderer';
import {PeriodEditor}              from '@/helpers/period.editor';
import {DurationRenderer}          from '@/helpers/duration.renderer';
import {DurationEditor}            from '@/helpers/duration.editor';
import {WorkBlockChange}           from '@/screens/ide/workblox/work.block.change';

import {IWorkInputCapture}         from './i.work.input.capture';

import {ICommand}                  from '@/util/i.command';
import {SetTypeCommand}            from './set.type.command';
import {SetNameCommand}            from './set.name.command';
import {SetDescriptionCommand}     from './set.description.command';
import {SetDaysCommand}            from './set.days.command';
import {SetDurationCommand}        from './set.duration.command';
import {SetBeginCommand}           from './set.begin.command';
import {SetStartLocCommand}        from './set.start.loc.command';
import {SetEndLocCommand}          from './set.end.loc.command';

import {WorkBlockInlineEdit} from './work.block.inline.edit';

import 'ag-grid-community';

import {
  CellClassParams,
  CellPosition,
  CellValueChangedEvent,
  Column,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  TabToNextCellParams
} from "ag-grid-community";

@Component({
  selector: 'work-blox-editor',
  styleUrls: ['./work.blox.editor.scss'],
  template: `
    <div
      id="work-blox-container-wrapper_{{uuid}}"
      class="work-blox-container-wrapper"
      style="display: block; float: left; padding: 8px; width: 100%; height: 100%;">

      <span style="height: 32px; width: 100%; display: block;" [innerHTML]="errorText"></span>
      <ag-grid-angular
        id="ag-grid-work-blox-editor_{{uuid}}" style="width: 100%; height: calc(100% - 32px);"
        class="ag-theme-balham-dark ag-grid-work-blox-editor"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        [tabToNextCell]="tabToNextCell"

        [components]="components"

        (gridReady)="onGridReady($event)"

      >
      </ag-grid-angular>
    </div>

   `
})

/**
 *
 * @class WorkBloxEditor
 *
 * @brief editor for just the work blocks section of a scenario
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class WorkBloxEditor implements AfterViewInit, OnDestroy, IWorkInputCapture {

  public          errorText: string                               = '<i class="wbedit-success-line">Ready.</i>';

  @Input()             uuid: number                               = 0;

  /** the history manager (not owned by us */
  private          _history: UnDoRedo                             = null;

  /** @var _scenario the scenario model */
  private         _scenario: Scenario                             = null;

  /** the currently active row/selection/workblock */
  private selectedWorkBlock: BehaviorSubject<WorkBlockChange>     = null;
  private         selectSub: Subscription                         = null;

  private    lastInlineEdit: BehaviorSubject<WorkBlockInlineEdit> = null;
  private           editSub: Subscription                         = null;

  /** validation related flags */
  private       _inputValid: boolean                              = false;
  private        _canDelete: boolean                              = false;
  private         _hasInput: boolean                              = false;

  /** the API for the widget */
  private               api: GridApi                              = null;

  private      isFullScreen: boolean                              = false;

  public context  = {

    workblox: this

  };

  public defaultColDef           = {
    sortable: true,
    filter: true,
    resizable: true
  };

  public rowData                 = [{
    type: '',
    name: '',
    start: '',
    end: '',
    begin: '',
    duration: '',
    desc: ''
  }];

  @Output() edit                 = new EventEmitter<Scenario>();

  public components = {
    dpEditor:       WorkDecisionPointEditor,
    dayEditor:      WorkActiveDaysEditor,
    periodEditor:   PeriodEditor,
    durationEditor: DurationEditor
  };

  public columnDefs: any[]       = [
    {
      headerName:       'ID',
      field:            'id',
      editable: false,
      width: 150
    },
    {
      headerName:       'Type',
      field:            'type',
      cellRenderer:     WorkTypeRenderer,
      cellEditor:       'agRichSelectCellEditor',
      cellEditorParams: {
        cellRenderer:    WorkTypeRenderer,
        values: [
          {name: 'MegaBlock',    code: WorkType.MEGABLOCK},
          {name: 'Construction', code: WorkType.CONSTRUCTION}
        ]
      },
      cellDataType: 'object',

      equals:           (a, b): boolean =>  {

        if ((typeof a !== 'object') || (typeof b !== 'object')) {

          return a === b;
        }

        return a.name === b.name;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.typeChanged(e.oldValue.code, e.newValue.code);
        e.context.workblox.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable: true,
      width: 150
    },
    {
      headerName:   'Name',
      field:        'name',
      cellRenderer: NameRenderer,
      cellDataType: 'text',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.nameChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === ''));
        }
      },

      editable:     true,
      width:        150
    },
    {
      headerName:   'Start Location',
      field:        'start',
      cellRenderer: WorkDecisionPointRenderer,
      cellEditor:   'dpEditor',
      cellDataType: 'object',
      equals:       (a: DecisionPoint, b: DecisionPoint): boolean =>  {

        if (!( a instanceof DecisionPoint ) || !( b instanceof DecisionPoint )) {

          return a === b;
        }

        return a._id === b._id;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.startLocChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable:     true,
      width:        150
    },
    {
      headerName:   'End Location',
      field:        'end',
      cellRenderer: WorkDecisionPointRenderer,
      cellEditor:   'dpEditor',
      cellDataType: 'object',
      equals:       (a: DecisionPoint, b: DecisionPoint): boolean =>  {

        if (!( a instanceof DecisionPoint ) || !( b instanceof DecisionPoint )) {

          return a === b;
        }

        return a._id === b._id;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.endLocChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable:     true,
      width:        150
    },
    {
      headerName:   'Begin Time',
      field:        'begin',
      cellRenderer:  DurationRenderer,
      cellEditor:   'durationEditor',
      cellDataType: 'object',
      equals:       (a: Period, b: Period): boolean =>  {

        if (!( a instanceof Period ) || !( b instanceof Period )) {

          return a === b;
        }

        return a.asString() === b.asString();
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.beginChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable:     true,
      width:        150
    },
    {
      headerName:  'Duration',
      field:       'duration',
      cellRenderer: DurationRenderer,
      cellEditor:   'durationEditor',
      cellDataType: 'object',
      equals:       (a: Period, b: Period): boolean =>  {

        if (!( a instanceof Period ) || !( b instanceof Period )) {

          return a === b;
        }

        return a.asString() === b.asString();
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.durationChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable:     true,
      width:        150
    },
    {
      headerName:   'Days',
      field:        'days',
      cellRenderer: WorkActiveDaysRenderer,
      cellEditor:   'dayEditor',
      cellDataType: 'array',
      equals:       (a: number[], b: number[]): boolean =>  {

        if (!Array.isArray(a) || !Array.isArray(b)) {

          return a === b;
        }

        a.sort();
        b.sort();

        return JSON.stringify(a) === JSON.stringify(b);
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          if (Array.isArray(params.value) && params.value.length === 0) {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.daysChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      editable:     true,
      width:        150
    },
    {
      headerName:   'Description',
      cellRenderer: DescriptionRenderer,
      field:        'desc',
      cellDataType: 'text',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.workblox.descriptionChanged(e.oldValue, e.newValue);
        e.context.workblox.validate();
      },

      editable:     true,
      width:        150
    }
  ];

  constructor(

  ) {

  }

  /** update the error line in the loader drop down */
  public status(msg: string =  '') {

    this.errorText = `<i class="wbedit-info-line">${msg}</i>`;
    Logger.info(`[workbloxeditor] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public success(msg: string =  '') {

    this.errorText = `<i class="wbedit-success-line">${msg}</i>`;
    Logger.debug(`[workbloxeditor] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public error(msg: string =  '') {

    this.errorText = `<i class="wbedit-error-line">${msg}</i>`;
    Logger.error(`[workbloxeditor] ${msg}`);
  }

  /** scan all the editor inputs, and set up our validity flags */
  private validate(): void {

    const  fields: string[] = ['days', 'duration', 'begin', 'end', 'start', 'name', 'type'];
    const     row: IRowNode = this.firstRow();
    let   invalid: boolean  = false;

    this.status('Validating...');

    /* if it has non-zero workblock id...then its one we can either clone or delete */

    this._canDelete = this.currentId() > 0;

    /* if any of the inputs are non-empty (not '', null or undefined), then we have "some" input */

    this._hasInput = false;

    fields.forEach((item: string) => {

      const datum: any = row.data[item];

      if (datum === '') {

        this.error(`${item} can not be empty.`);

        invalid = true;
        return ;
      }

      if (typeof datum === 'undefined') {

        this.error(`${item} can not be empty.`);

        invalid = true;
        return ;
      }

      if (datum === null) {

        this.error(`${item} can not be empty.`);

        invalid = true;
        return ;
      }

      if (Array.isArray(datum) && (datum.length === 0)) {

        this.error(`${item} (list) can not be empty.`);

        invalid = true;
        return ;
      }

      /* at least one column has non-empty input */

      this._hasInput = true;
    });

    if (!invalid) {

      this.success('Validated: OK.');
    }

    /* the input is only valid if all inputs are filled in */

    this._inputValid = !invalid;
  }

  /** check to see if the input is valid (can add as WorkBlock) */
  public isValid(): boolean {

    return this._inputValid;
  }

  /** check to see if the input is an edit of an existing workblock (i.e. ID > 0) */
  public isDeletable(): boolean {

    return this._canDelete;
  }

  /** check to see if the there is any input, either user entered or a workblock being edited. */
  public hasInput(): boolean {

    return this._hasInput || this._canDelete;
  }

  tabToNextCell(params: TabToNextCellParams): CellPosition  {

    /* if we're going outside the table...stay on target, stay on target! */

    if ( (typeof params.nextCellPosition === 'undefined') || (params.nextCellPosition === null)) {

      return params.previousCellPosition;
    }

    return params.nextCellPosition;
  }

  onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;

    this.focusCell('type');
  }

  /** capture the current user inputs */
  captureInput(): WorkBlockChange {

    /* there is only one row, get the data from the row, and convert to a snapshot */

    const snapshot: WorkBlockChange = new WorkBlockChange();
    const row: IRowNode             = this.firstRow();

    let type: WorkType = null;

    if (row.data.type !== null) {

      if (typeof row.data.type === 'object') {

        type = row.data.type.code;

      }
    }

    snapshot.id          = row.data.id       ? row.data.id       : '';
    snapshot.type        = type;
    snapshot.name        = row.data.name     ? row.data.name     : '';
    snapshot.start       = row.data.start    ? row.data.start    : null;
    snapshot.end         = row.data.start    ? row.data.end      : null;
    snapshot.begin       = row.data.begin    ? row.data.begin    : null;
    snapshot.duration    = row.data.duration ? row.data.duration : null;
    snapshot.days        = row.data.duration ? row.data.days     : [];
    snapshot.description = row.data.desc     ? row.data.desc     : '';

    /* pass it back! */

    return snapshot;
  }

  /** helper to focus on the given column / field */
  focusCell(colName: string) {

    let     cols: Column[]  = this.api.getAllDisplayedColumns();
    let col: Column         = cols.find(x => x.getId() === colName);

    /* find the column ... */

    if ((col === null) || (typeof col === 'undefined')) {

      return ;
    }

    /* focus on that cel */

    this.api.ensureColumnVisible(col);
    this.api.setFocusedCell(0, col);
  }

  /** restore the user input to what they are in this snapshot */
  restoreInput(snapshot: WorkBlockChange): void {

    this.status(`Loading input...`);

    Logger.debug('[workbloxeditor] restoring input: ', snapshot);

    /* there is only one row, set the values from this snapshot, without change detection */

    /* create the row data (there is only one row!) */

    let type: any = null;

    if (snapshot.type === WorkType.MEGABLOCK) {

      type = {name: 'MegaBlock',    code: WorkType.MEGABLOCK};

    } else if (snapshot.type === WorkType.CONSTRUCTION) {

      type = {name: 'Construction', code: WorkType.CONSTRUCTION};

    } else if (typeof snapshot.type === 'undefined') {

      type = '';
    }

    /* non-change detection update */

    const row: IRowNode = this.firstRow();

    row.data.id         = snapshot.id;
    row.data.type       = type;
    row.data.name       = snapshot.name;
    row.data.start      = snapshot.start;
    row.data.end        = snapshot.end;
    row.data.begin      = snapshot.begin;
    row.data.duration   = snapshot.duration;
    row.data.days       = snapshot.days;
    row.data.desc       = snapshot.description;

    this.status(`Loaded.`);

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

  }

  /** handle single column edits in the edit input area */
  setInlineEditSubject(edit: BehaviorSubject<WorkBlockInlineEdit>) {

    if (this.editSub !== null) {

      /* unsubscribe old one */

      this.editSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.lastInlineEdit = edit;
    this.editSub        = null;

  }

  /** set a new observer to use for watching for selection changes */
  public setSelectionSubject(selection: BehaviorSubject<WorkBlockChange>) {

    if (this.selectSub !== null) {

      /* unsubscribe old one */

      this.selectSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.selectedWorkBlock = selection;
    this.selectSub         = selection.subscribe( (next: WorkBlockChange) => {

      this.setSelected(next);
    });

  }

  /** handle somebody change the selection somewhere */
  public setSelected(change: WorkBlockChange): void {

    if (change === null) {

      /* just clear any open edits */

      this.clear();

      return ;
    }

    if (change.snapshot !== null) {

      /* this selection has a capture of previous user inputs, we use those instead */

      change = change.snapshot;
    }

    /* check that we aren't calling ourselves */

    if ( (change.src instanceof WorkBloxEditor) && (change.src === this)) {

      /* from self, ignore infinite looping! */

      return ;
    }

    /* handle the selection update */
    Logger.debug('[workbloxeditor] selection change....');

    this.restoreInput(change);
  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    const context: any[] = [];



    return context;
  }

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[workbloxeditor] view init...');

    this.clear();

  }

  ngOnDestroy(): void {

    if (this.selectSub !== null) {

      /* unsubscribe our observer */

      this.selectSub.unsubscribe();
    }

    if (this.editSub !== null) {

      /* unsubscribe our observer */

      this.editSub.unsubscribe();
    }
  }

  /** clear the user input area */
  public clear() {

    Logger.debug('[workbloxeditor] clearing...');

    if (!this.api) {

      /* api isn't even available yet */

      return ;
    }

    /* clearing just means all input columns are empty */

    this.api.setGridOption('rowData', [{
      type: '',
      name: '',
      start: '',
      end: '',
      begin: '',
      duration: '',
      desc: ''
    }]);
  }

  /** fetch current row ID (if there is one */
  public currentId(): number {

    const row: IRowNode  = this.firstRow();

    const result: number = row.data?.id;

    if (result === null) {

      return 0;
    }

    if (typeof result === 'undefined') {

      return 0;
    }

    if ( isNaN(result) ) {

      return 0;
    }

    /* looks legit, pass it back */

    return result;
  }

  /** fetch the first row */
  public firstRow(): IRowNode {

    const items: IRowNode[] = [];

    this.api.forEachNode((rowNode: IRowNode): void => {

      items.push(rowNode);
    });

    return items[0];
  }

  /** helper to fetch the actual WorkBlock for the given row */
  public blockOfRow(id: number): WorkBlock {

    if (this._scenario === null) {

      return null;
    }

    return Array.from(this._scenario.workBlocks().values()).find(x => x._id === id);
  }

  /** helper to update the actual WorkBlock model in the scenario */
  public updateModel(id: number, field: string, value: any): void {

    const block: WorkBlock = this.blockOfRow(id);

    if ((block === null) || (!block)) {

      /* can't find it */

      return ;
    }

    Logger.debug('[workbloxeditor] updating model:', id, field, value);

    switch (field) {

      case 'type': {

          /* use the given type code (WorkType) */

          block._type = value as WorkType;
        }
        break;

      case 'name': {

          /* use the given name string */

          block._name = value as string;
        }
        break;

      case 'description': {

          /* use the given description */

          block._description = value as string;
        }
        break;

      case 'start': {

          /* convert period to seconds */

          const   p: Period = value as Period;
          const sec: number = p.asSeconds();

          block._start = sec;
        }
        break;

      case 'duration': {

          /* convert period to seconds */

          const   p: Period = value as Period;
          const sec: number = p.asSeconds();

          block._duration = sec;
        }
        break;

      case 'days': {

          /* use the given day number array */

          block._days = value as number[];
        }
        break;

      case 'from': {

          /* use the given west side decision point */

          block._from = value as DecisionPoint;
        }
        break;

      case 'to': {

          /* use the given east side decision point */

          block._to = value as DecisionPoint;
        }
        break;
    }

    const edit: WorkBlockInlineEdit =  new WorkBlockInlineEdit({
      id,
      field,
      value
    });

    /* publish the edit we just did...so that other views know to update themselves */

    this.lastInlineEdit.next(edit);

  }

  /** react to a work block begin change */
  public startLocChanged(a: DecisionPoint, b: DecisionPoint): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'from', b);
    }

    /* track the change */

    const cmd: ICommand = new SetStartLocCommand(a, b, this);

    this._history.insert(cmd);

    /* publish the edit so other views can update themselves */



  }

  /** force the work begin column to be this value */
  public setStartLoc(value: DecisionPoint): void {

    let datum: any = null;

    if (value instanceof DecisionPoint) {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.start = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('from');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'from', datum);
    }
  }

  /** react to a work block begin change */
  public endLocChanged(a: DecisionPoint, b: DecisionPoint): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'to', b);
    }

    /* track the change */

    const cmd: ICommand = new SetEndLocCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the work begin column to be this value */
  public setEndLoc(value: DecisionPoint): void {

    let datum: any = null;

    if (value instanceof DecisionPoint) {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.end = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('to');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'to', datum);
    }
  }

  /** react to a work block begin change */
  public beginChanged(a: Period, b: Period): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start', b);
    }

    /* track the change */

    const cmd: ICommand = new SetBeginCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the work begin column to be this value */
  public setBegin(value: Period): void {

    let datum: any = null;

    if (value instanceof Period) {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.begin = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('start');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start', datum);
    }
  }

  /** react to a work block duration change */
  public durationChanged(a: Period, b: Period): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'duration', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDurationCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the active duration column to be this value */
  public setDuration(value: Period) {

    let datum: any = null;

    if (value instanceof Period) {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.duration = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('duration');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'duration', datum);
    }
  }

  /** react to a work block active days change */
  public daysChanged(a: number[], b: number[]): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'days', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDaysCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the active days column to be this value */
  public setDays(value: number[]): void {

    let datum: any = null;

    if (Array.isArray(value)) {

      datum = value;

    } else if (typeof value === 'undefined') {

      datum = [];

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.days = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('days');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'days', datum);
    }
  }

  /** react to a work block type change */
  public typeChanged(a: WorkType, b: WorkType): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'type', b);
    }

    /* track the change */

    const cmd: ICommand = new SetTypeCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the type column to be this value */
  public setType(value: WorkType) {

    let datum: any = null;

    if (value === WorkType.MEGABLOCK) {

      datum = {name: 'MegaBlock',    code: WorkType.MEGABLOCK};

    } else if (value === WorkType.CONSTRUCTION) {

      datum = {name: 'Construction', code: WorkType.CONSTRUCTION};

    } else if (typeof value === 'undefined') {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.type = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('type');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'type', datum);
    }
  }

  /** react to a workblock name change */
  public nameChanged(a: string, b: string): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'name', b);
    }

    /* track the change */

    const cmd: ICommand = new SetNameCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the name column to be this value */
  public setName(value: string) {

    if (typeof value === 'undefined') {

      value = '';
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.name = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('name');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'name', value);
    }
  }

  /** react to a workblock description change */
  public descriptionChanged(a: string, b: string): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'description', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDescriptionCommand(a, b, this);

    this._history.insert(cmd);
  }


  /** force the description column to be this value */
  public setDescription(value: string) {

    if (typeof value === 'undefined') {

      value = '';
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.desc = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('description');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'description', value);
    }
  }

  /** take a reference to the shared history manager (provides undo/redo support) */
  public setHistory(history: UnDoRedo): void {

    this._history = history;
  }

  /** reset to this scenario */
  public setScenario(s: Scenario) {

    /* set the "model" */

    this._scenario = s;

    /* clear the input area */

    this.clear();

    this.status(`New scenario set.`);
  }

  /** fetch the current scenario (if there is one) */
  public getScenario(): Scenario {

    return this._scenario;
  }


}
